import { z } from 'zod';
import type { EEventType } from '../events.enum';
import type { TTemplateInterviewSummary } from './templateInterviewSummary.types';
import {
  type TAvailableLanguageCodes,
  type TNode,
  type TemplateEmailSettingsDocument,
  type TemplateSmsSettingsDocument,
  type TemplateSettingsDocument,
  type TemplateReminderSettingsDocument,
  type TemplateVoiceSettingsDocument,
} from '@hubertai/screening-question-types';
import type { TBranding } from 'types/branding.types';

export enum ETemplateType {
  Screening = 'screening',
  Intro = 'intro',
  Exit = 'exit',
}
export const zTemplateTypeEnum = z.nativeEnum(ETemplateType);
export const templateTypesList: { label: string; value: string }[] = [
  { label: 'Screening', value: ETemplateType.Screening },
  { label: 'Intro', value: ETemplateType.Intro },
  { label: 'Exit', value: ETemplateType.Exit },
];

export enum ECurrencyCode {
  EUR = 'EUR',
  USD = 'USD',
  GBP = 'GBP',
  SEK = 'SEK',
  DKK = 'DKK',
  NOK = 'NOK',
}
export const zCurrencyCodeEnum = z.enum(ECurrencyCode);
export const currencyCodesList: { label: string; value: string }[] = [
  { label: 'EUR', value: ECurrencyCode.EUR },
  { label: 'USD', value: ECurrencyCode.USD },
  { label: 'GBP', value: ECurrencyCode.GBP },
  { label: 'SEK', value: ECurrencyCode.SEK },
  { label: 'DKK', value: ECurrencyCode.DKK },
  { label: 'NOK', value: ECurrencyCode.NOK },
];

type TAllowedLanguages = {
  code: string;
};

export type TLanguageSettings = {
  starting_language: TAvailableLanguageCodes;
  list: TAvailableLanguageCodes[];
  allowed_languages: TAllowedLanguages[];
};

export type TTemplateBlock = {
  id: string;
  name: string;
  description: string;
  is_open: boolean;
  editable: boolean;
  nodes: TNode[];
};

/* Template blocks (plural) */
type TTemplateBlocks = {
  ids?: [string];
  map?: {
    [key: string]: TTemplateBlock;
  };
  blocks: TTemplateBlock[];
};

export type TTemplateChangeLogItem = {
  id: string;
  created_at: string;
  actor: 'user' | 'system';
  actor_id?: string; // Optional ID for the user or system that made the change
  actor_username?: string; // Optional ID for the user or system that made the change
  action_type: EEventType; // Describes the action, e.g., "updated", "deleted", "created"
  action_id?: string;
  field_changed?: string; // Optional field name that was changed, e.g., "name"
  old_value?: any; // Previous value of the field
  new_value?: any; // New value of the field
  value?: any; // some aren't updates
  message?: string; // Optional additional context or notes
};

// TODO: Revisit this when time allows.
export type TTemplateParameterKeys =
  | 'intro'
  | 'text'
  | 'outro'
  | 'clarify'
  | 'pre_actions'
  | 'post_actions'
  | 'rules'
  | 'options'
  | 'max_selections'
  | 'min_selections'
  | 'min_required';

// Base Node parameter values that can appear at both node and form level
type TBaseNodeParameterValue = {
  // intro?: Partial<{ [key in TAvailableLanguageCodes]: string[] }>;
  // text?: Partial<{ [key in TAvailableLanguageCodes]: string[] }>;
  // outro?: Partial<{ [key in TAvailableLanguageCodes]: string[] }>;
  // clarify?: Partial<{ [key in TAvailableLanguageCodes]: string[] }>;
  // pre_actions?: string[];
  // post_actions?: string[];
  // rules?: TQuestionRule[];
  [key: string]: any;
};

// Node parameter values including both common values and form IDs
type TNodeParameterValue = TBaseNodeParameterValue;
// & { [id in TFormIdBranded]?: TFormParameterValue; };

// Full template parameters type
export type TTemplateParameters = {
  [key: string]: TNodeParameterValue & {
    options?: {
      [optionId: string]: {
        enabled?: { [lang in TAvailableLanguageCodes]?: boolean };
        replies?: { [lang in TAvailableLanguageCodes]?: string[] };
        label?: { [lang in TAvailableLanguageCodes]?: string };
        evaluation?: string;
        bonus_points?: number;
      };
    };
  };
};

const _VISIBILITY = ['global', 'client', 'local'] as const;
export type TVisibility = (typeof _VISIBILITY)[number];

export type TTemplate = {
  _id: string;
  process_id: string;
  template_reference_id: string;
  name: string;
  description: string;
  client_id?: string;

  language_settings: TLanguageSettings;

  role: {
    id: string;
    name: string;
  };
  role_id: string;

  company: string;
  organization: {
    name: string;
    department: string;
  };
  salary_range?: {
    min: number;
    max: number;
    currency: ECurrencyCode;
  };

  title: string;
  position: {
    form_of_employment: string;
  };

  threshold: number;
  estimated_length: number;
  published: boolean;
  default: boolean;
  candidate_app: boolean;
  script: boolean;
  is_draft: boolean;
  is_current: boolean;
  is_archived?: boolean;
  is_deleted?: boolean;
  mother_template?: boolean;
  immediately_notify_unscored: boolean;

  blocks: TTemplateBlocks;
  interview_summary?: TTemplateInterviewSummary;
  outline: {
    [key: string]: any;
    question_list: string[];
  };
  // parameters: {
  //   [key: string]: {
  //     intro?: Partial<{ [key in TAvailableLanguageCodes]: string[] }>;
  //     text?: Partial<{ [key in TAvailableLanguageCodes]: string[] }>;
  //     outro?: Partial<{ [key in TAvailableLanguageCodes]: string[] }>;
  //     clarify?: Partial<{ [key in TAvailableLanguageCodes]: string[] }>;
  //     pre_actions?: string[];
  //     post_actions?: string[];
  //     rules?: any;
  //   };
  // };
  parameters: TTemplateParameters;
  evaluation: {
    scoring_categories: ScoringCategories;
    [key: string]: any;
  };
  evaluationSummary: any;
  scoringCategories: ScoringCategories;

  templateOutline: {
    numberOfQuestions: number;
  };

  feature_flags?: {
    [key: string]: unknown;
  };
  branding?: TBranding;

  version: number;
  visibility?: TVisibility;
  change_log: TTemplateChangeLogItem[];

  created: {
    date: Date;
    by: string | undefined;
    first_name: string | undefined;
    last_name: string | undefined;
  };

  updated: {
    date: Date;
    by: string | undefined;
    first_name: string | undefined;
    last_name: string | undefined;
  };

  accessGroupIds?: string[];
  theme_id?: string;

  email_settings?: TemplateEmailSettingsDocument;
  sms_settings?: TemplateSmsSettingsDocument;
  reminder_settings?: TemplateReminderSettingsDocument[];
  settings?: TemplateSettingsDocument;
  voice_settings?: TemplateVoiceSettingsDocument;

  // Old (interviews_definitions)
  questions: string[];
};

type ScoringCategories = {
  [key: string]: any;
  list: [];
  structure: object;
};
